/**
 * Gera CSV (BOM UTF-8 + delimiter `;` por padrão para abrir limpo no Excel
 * brasileiro) a partir de uma lista de objetos. Dispara download no browser.
 *
 * Os headers são derivados da primeira linha OU do array `columns` explícito.
 * Quando passado `columns`, controla a ordem e o cabeçalho amigável.
 */
export interface CsvColumn<T> {
  key: keyof T | ((row: T) => string | number | null | undefined);
  header: string;
}

function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  // Quebra de linha, ponto-e-vírgula e aspas exigem quoting.
  if (/[\n\r";]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function downloadCsv<T>(
  filename: string,
  rows: T[],
  columns?: Array<CsvColumn<T>>,
): void {
  const cols: Array<CsvColumn<T>> =
    columns ??
    (rows.length > 0
      ? (Object.keys(rows[0] as object) as Array<keyof T>).map((k) => ({
          key: k,
          header: String(k),
        }))
      : []);
  if (cols.length === 0) return;

  const headerLine = cols.map((c) => escapeCell(c.header)).join(";");
  const dataLines = rows.map((row) =>
    cols
      .map((c) => {
        const value = typeof c.key === "function" ? c.key(row) : (row[c.key as keyof T] as unknown);
        return escapeCell(value);
      })
      .join(";"),
  );

  // BOM UTF-8 garante acentuação correta no Excel.
  const csv = "﻿" + [headerLine, ...dataLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
